import Joi from "joi";
import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import Toast from "../../helpers/Toast";
import { CheckListModelDTO } from "../../models/CheckListModel/CheckListModelDTO";
import { CheckListModelTaskDTO } from "../../models/CheckListModelTask/CheckListModelTaskDTO";
import { EditCheckListModelDTO, EditCheckListModelDTOSchema } from "../../models/CheckListModel/EditCheckListModelDTO";
import { CheckListModelService } from "../../services/CheckListModelService";
import useValidationSchema, { FieldConfig } from "../../components/Validations";
import CheckboxComponent from "../../components/CheckboxComponent";
import ModalComponent from "../../components/ModalComponent";
import { Utilities } from '../../helpers/Utilities';
import { SortingRule } from "react-table";
import SelectComponent from "../../components/SelectComponent";
import { CheckListModelTaskService } from "../../services/CheckListModelTaskService";
import Routes from '../../helpers/Routes';
import { CheckListModelNavBar } from "../../components/CheckListModels/CheckListModelNavBar";


export default function CheckListModelEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [model, setModel] = useState<CheckListModelDTO>({} as CheckListModelDTO);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const checkListModelService = new CheckListModelService();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pageSizes: number[] = [5, 10, 15, 30];
    const checkListModelTaskService = new CheckListModelTaskService();

    const [data, setData] = useState<CheckListModelTaskDTO[]>([]);
    const [pageCount, setPageCount] = useState(1);

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 10));

    const [goToPage, setGoToPage] = useState<number>();


    const schemaClientValidation = EditCheckListModelDTOSchema;


    const getModel = async () => {
        setLoading(true);
        const response = await checkListModelService.GetById(Number(id));
        if (!response.success || !response.obj) {
            Toast.Show("error", response.message);
            setLoading(false);
            return;
        }
        setModel(response.obj);
        setLoading(false);
    };

    useEffect(() => {
        getModel();
    }, [id]);

    const editModel = async () => {
        setLoading(true);

        const updatedModel: EditCheckListModelDTO = {
            id: Number(id),
            title: model.title,
            brand: model.brand,
            model: model.model,
            active: model.active,
        };

        const { error } = schemaClientValidation.validate(updatedModel);
        if (error) {
            Toast.Show("error", error.details.map(d => d.message).join(", "));
            setLoading(false);
            return;
        }


        const response = await checkListModelService.Edit(updatedModel);
        setLoading(false);

        if (!response.success) {
            Toast.Show("error", response.message);
            return;
        }

        navigate(Routes.checkListModels.list);
        Toast.Show("success", response.message);
    };

    const deleteModel = async () => {
        setLoading(true);
        const response = await checkListModelService.Delete(Number(id));
        setLoading(false);

        if (!response.success) {
            Toast.Show("error", response.message);
            return;
        }

        Toast.Show("success", response.message);
        navigate(Routes.checkListModels.list);
    };



    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle
                        title={`${intl.formatMessage({ id: "Screens.CheckListModel.EditTitle" })} ${id}`}
                        allowGoBack={true}
                        onGoBackClick={() => navigate(Routes.checkListModels.list)}
                    />
                </div>
                <div className="pageBody">
                    <Row>
                        {/* 🟩 Metade esquerda: Dados do Modelo */}
                        <Col xl={6} lg={12} className="mb-3">
                            <label style={{ margin: "0px", padding: "13px" }}>
                                <b>Dados do Modelo de Checklist</b>
                            </label>

                            <Row style={{ marginBottom: "10px" }}>
                                <InputComponent
                                    xl={12} lg={12} md={12} sm={12} xs={12}
                                    type="text"
                                    value={model.title}
                                    label={intl.formatMessage({ id: "Screens.CheckListModel.Info.Title" })}
                                    onChange={(t: string) => setModel({ ...model, title: t })}
                                />
                            </Row>
                            <Row style={{ marginBottom: "10px" }}>
                                <InputComponent
                                    xl={6} lg={6} md={6} sm={12} xs={12}
                                    type="text"
                                    value={model.brand}
                                    label={intl.formatMessage({ id: "Screens.CheckListModel.Info.Brand" })}
                                    onChange={(t: string) => setModel({ ...model, brand: t })}
                                />
                                <InputComponent
                                    xl={6} lg={6} md={6} sm={12} xs={12}
                                    type="text"
                                    value={model.model}
                                    label={intl.formatMessage({ id: "Screens.CheckListModel.Info.Model" })}
                                    onChange={(t: string) => setModel({ ...model, model: t })}
                                />
                            </Row>
                            <Row style={{ marginBottom: "10px" }}>
                                <Col xl={12} className="d-flex align-items-center">
                                    <CheckboxComponent
                                        checked={model.active}
                                        label={intl.formatMessage({ id: "Screens.CheckListModel.Info.Active" })}
                                        onChange={(checked: boolean) => setModel({ ...model, active: checked })}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ marginTop: "20px" }}>
                                <Col xs={12} className="d-flex gap-2 flex-wrap">
                                    <ButtonComponent
                                        disabled={loading}
                                        loading={loading}
                                        text={intl.formatMessage({ id: "CheckListModelEdit.Save" })}
                                        onClick={editModel}
                                    />
                                    <ButtonComponent
                                        disabled={loading}
                                        text={intl.formatMessage({ id: "Screens.Delete" })}
                                        className="btn-danger"
                                        onClick={() => setShowDeleteConfirm(true)}
                                    />
                                </Col>
                            </Row>
                        </Col>

                        {/* 🟦 Metade direita: Navbar alinhada com a linha “Dados do Modelo” */}
                        <Col xl={6} lg={12} className="d-flex align-items-start">
                            <CheckListModelNavBar />
                        </Col>
                    </Row>
                </div>
            </div>

            <ModalComponent
                IsOpen={showDeleteConfirm}
                title={intl.formatMessage({ id: "Screens.CheckListModel.Modal.Title" })}
                text={intl.formatMessage({ id: "Screens.CheckListModel.ConfirmDeleteText" })}
                buttonLeft={intl.formatMessage({ id: "General.Cancel" })}
                buttonRight={intl.formatMessage({ id: "General.Delete" })}
                OnClickLeft={() => setShowDeleteConfirm(false)}
                OnClickRight={() => {
                    setShowDeleteConfirm(false);
                    deleteModel();
                }}
            />
        </Layout>
    );
}